import { Analytics, setAnalyticsContext } from '@inboundx/shared/analytics';
import { logger } from '@inboundx/shared/platform';
import { ArrowRight, ExternalLink, MessageCircle } from 'lucide-react';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

export default function CTATestPage() {
    // Set up analytics context for testing
    useEffect(() => {
        // Use localhost for testing - needs CTAs configured in site config database
        // This simulates the analytics context that would be set by the chat widget
        setAnalyticsContext({
            domain: 'localhost', // Using localhost for testing
            sessionId: `test-session-${Date.now()}`,
            apiVersion: '1.0.0',
            frontendVersion: '1.0.0',
            deploymentTarget: 'preprod',
            environment: 'development',
        });

        logger.debug('🎯 CTA Test Page - Analytics context set for form tracking test');
    }, []);

    const handleCTAClick = async (ctaInfo: { url: string; label: string; language: string }) => {
        logger.debug('🖱️ CTA button clicked:', ctaInfo);

        // Track the CTA click (this will use sendBeacon for guaranteed delivery)
        try {
            await Analytics.trackCTAClicked({
                language: ctaInfo.language,
                url: ctaInfo.url,
                label: ctaInfo.label,
            });
            logger.debug('✅ CTA click tracked successfully');
        } catch (error) {
            logger.error('❌ Failed to track CTA click:', error);
        }
    };

    // Test CTA configurations - including new button click detection tests
    const testCTAs = [
        {
            id: 'demo-request',
            url: '/form-test/demo',
            label: 'Request Demo',
            language: 'en',
            description: 'Test traditional form submission tracking',
        },
        {
            id: 'contact-form',
            url: '/form-test/contact',
            label: 'Contact Us',
            language: 'en',
            description: 'Test contact form with preventDefault() handling',
        },
        {
            id: 'ajax-form',
            url: '/form-test/ajax',
            label: 'AJAX Form Test',
            language: 'en',
            description: 'Test new button click detection for AJAX forms',
        },
        {
            id: 'mayday-simulation',
            url: '/form-test/mayday',
            label: 'Mayday-Style Form',
            language: 'en',
            description: 'Simulate Mayday form behavior with JavaScript submission',
        },
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-8">
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="text-center mb-12">
                    <h1 className="text-4xl font-bold text-gray-900 mb-4">
                        📊 CTA Analytics Test Page
                    </h1>
                    <p className="text-xl text-gray-600 mb-6">
                        Test Call-to-Action buttons that trigger form submission tracking
                    </p>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 max-w-2xl mx-auto">
                        <div className="flex items-start">
                            <MessageCircle className="h-5 w-5 text-yellow-600 mt-0.5 mr-2 flex-shrink-0" />
                            <div className="text-sm text-yellow-800">
                                <strong>How it works:</strong> Click a CTA button below to navigate
                                to a form page. The analytics system will track the CTA click and
                                then automatically detect form submissions on the destination page.
                            </div>
                        </div>
                    </div>
                </div>

                {/* Navigation Back */}
                <div className="mb-8">
                    <Link
                        to="/"
                        className="inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors"
                    >
                        ← Back to Main App
                    </Link>
                </div>

                {/* CTA Test Cards */}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                    {testCTAs.map((cta) => (
                        <Card key={cta.id} className="p-6 hover:shadow-lg transition-shadow">
                            <div className="text-center">
                                <ExternalLink className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                                    {cta.label}
                                </h3>
                                <p className="text-gray-600 mb-6">{cta.description}</p>

                                {/* CTA Button that tracks analytics */}
                                <Link to={cta.url}>
                                    <Button
                                        className="w-full bg-blue-600 hover:bg-blue-700"
                                        onClick={() =>
                                            handleCTAClick({
                                                url: cta.url,
                                                label: cta.label,
                                                language: cta.language,
                                            })
                                        }
                                    >
                                        {cta.label}
                                        <ArrowRight className="ml-2 h-4 w-4" />
                                    </Button>
                                </Link>

                                <div className="mt-4 text-xs text-gray-500">
                                    Destination:{' '}
                                    <code className="bg-gray-100 px-1 rounded">{cta.url}</code>
                                </div>
                            </div>
                        </Card>
                    ))}
                </div>

                {/* Analytics Information */}
                <Card className="p-6 bg-blue-50 border-blue-200">
                    <h3 className="text-lg font-semibold text-blue-900 mb-4">
                        📈 Analytics Tracking Information
                    </h3>
                    <div className="space-y-2 text-sm text-blue-800">
                        <div>
                            <strong>CTA Click Event:</strong> <code>rw_cta_clicked</code>
                        </div>
                        <div>
                            <strong>Form Submit Event:</strong>{' '}
                            <code>rw_client_form_submitted</code>
                        </div>
                        <div>
                            <strong>Tracking Method:</strong> SendBeacon for reliable delivery
                        </div>
                        <div>
                            <strong>Analytics Context:</strong> Domain set to trigger form detection
                        </div>
                    </div>
                    <div className="mt-4 p-3 bg-white rounded border border-blue-200">
                        <div className="text-xs text-blue-600 font-medium mb-1">Expected Flow:</div>
                        <div className="text-xs text-blue-700">
                            1. Click CTA → Track <code>rw_cta_clicked</code>
                            <br />
                            2. Navigate to form page → Form listeners auto-setup
                            <br />
                            3. Submit form → Track <code>rw_client_form_submitted</code>
                        </div>
                    </div>
                </Card>
            </div>
        </div>
    );
}
